import { ReactNode } from "react";

import { FieldItem, GroupItem, OrderedItem } from "../types";

export const groupLength = ({
  group,
  fields,
  responses,
}: {
  group: GroupItem;
  fields: FieldItem[];
  responses: Record<string, any>;
}) => {
  if (!group.repeatable) return 1;

  return Math.max(
    ...fields.map((f) =>
      Array.isArray(responses[f.uri]) ? responses[f.uri].length : 1,
    ),
  );
};

export type RenderOrderedItemsOptions = {
  orderedItems: OrderedItem[];
  responses: Record<string, any>;
  renderGroup: (props: {
    key: string;
    group: GroupItem;
    length: number;
    fields: FieldItem[];
    children: ReactNode;
  }) => JSX.Element;
  renderGroupFieldSet: (props: {
    key: string;
    group: GroupItem;
    fields: FieldItem[];
    index: number;
    length: number;
    children: ReactNode;
  }) => JSX.Element;
  renderField: (props: {
    key: string;
    index?: number;
    field: FieldItem;
    value: any;
    group?: GroupItem;
  }) => JSX.Element | null;
};

export const renderOrderedItems = ({
  orderedItems,
  responses,
  renderGroup,
  renderGroupFieldSet,
  renderField,
}: RenderOrderedItemsOptions) =>
  orderedItems.flatMap((item) => {
    if (item.type === "field")
      return renderField({
        key: item.field.uri,
        field: item.field,
        value: responses[item.field.uri],
      });

    return renderGroup({
      // The key contains the length to trigger re-renders when deleting a repeatable group item
      key: `${item.group.uri}-${item.groupFieldSets.length}`,
      group: item.group,
      fields: item.fields,
      length: item.groupFieldSets.length,
      children: item.groupFieldSets.map((fieldSet) =>
        renderGroupFieldSet({
          key: `${item.group.uri}-${fieldSet.index}`,
          group: item.group,
          fields: item.fields,
          index: fieldSet.index || 0,
          length: item.groupFieldSets.length,
          children: fieldSet.visibleFields
            // Only display payment field in the first field set in a repeatable group
            .filter((f) => (!fieldSet.index ? true : f.type !== "payment"))
            .map((field) => {
              const value = Array.isArray(responses[field.uri])
                ? responses[field.uri][fieldSet.index || 0]
                : responses[field.uri];

              return renderField({
                key: item.group.repeatable
                  ? `${field.uri}-${fieldSet.index}`
                  : field.uri,
                index: fieldSet.index,
                field,
                group: item.group,
                value,
              });
            }),
        }),
      ),
    });
  });
